import React, { Suspense, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, type PaletteMode } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getConfig } from "./config";
import { OktaProvider } from "./auth/OktaProvider";
import { useAuth } from "./auth/useAuth";
import { setAccessTokenFn } from "./api/client";
import { getTheme } from "./theme";
import AppShell from "./components/AppShell";
import LoginCallback from "./pages/LoginCallback";

const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));

const Loading = (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

function AppLayout({
  darkMode,
  onToggleDarkMode,
}: {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}) {
  const auth = useAuth();

  useEffect(() => {
    setAccessTokenFn(auth.getAccessToken);
  }, [auth.getAccessToken]);

  return (
    <AppShell darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      <Routes>
        <Route
          path="/"
          element={<Suspense fallback={Loading}><DashboardPage /></Suspense>}
        />
        <Route path="/login/callback" element={<LoginCallback />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  const { envName } = getConfig();
  useEffect(() => {
    if (envName) {
      document.title = `SMRT Link News Feed [${envName.toUpperCase()}]`;
    }
  }, [envName]);

  const [mode, setMode] = useState<PaletteMode>("light");
  const theme = useMemo(() => getTheme(mode), [mode]);
  const queryClient = useMemo(() => new QueryClient(), []);

  const toggleDarkMode = () =>
    setMode((m) => (m === "light" ? "dark" : "light"));

  return (
    <BrowserRouter>
      <OktaProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppLayout darkMode={mode === "dark"} onToggleDarkMode={toggleDarkMode} />
          </ThemeProvider>
        </QueryClientProvider>
      </OktaProvider>
    </BrowserRouter>
  );
}
